import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { Notice } from "./components/Notice";
import { seedDemoData } from "./lib/db";
import { LandingPage } from "./pages/LandingPage";

const loadAboutPage = () =>
  import("./pages/AboutPage").then((module) => ({ default: module.AboutPage }));
const loadBrowseJobsPage = () =>
  import("./features/jobs/pages/BrowseJobsPage").then((module) => ({
    default: module.BrowseJobsPage,
  }));
const loadContractorProfilePage = () =>
  import("./features/contractors/pages/ContractorProfilePage").then((module) => ({
    default: module.ContractorProfilePage,
  }));
const loadDataPage = () =>
  import("./features/data/DataPage").then((module) => ({ default: module.DataPage }));
const loadJobDetailPage = () =>
  import("./features/jobs/pages/JobDetailPage").then((module) => ({
    default: module.JobDetailPage,
  }));
const loadNotFoundPage = () =>
  import("./pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage }));
const loadPostJobPage = () =>
  import("./features/post/pages/PostJobPage").then((module) => ({ default: module.PostJobPage }));
const loadSafetyPage = () =>
  import("./pages/SafetyPage").then((module) => ({ default: module.SafetyPage }));
const loadSettingsPage = () =>
  import("./features/settings/SettingsPage").then((module) => ({ default: module.SettingsPage }));

const AboutPage = lazy(loadAboutPage);
const BrowseJobsPage = lazy(loadBrowseJobsPage);
const ContractorProfilePage = lazy(loadContractorProfilePage);
const DataPage = lazy(loadDataPage);
const JobDetailPage = lazy(loadJobDetailPage);
const NotFoundPage = lazy(loadNotFoundPage);
const PostJobPage = lazy(loadPostJobPage);
const SafetyPage = lazy(loadSafetyPage);
const SettingsPage = lazy(loadSettingsPage);

const coreRoutePreloads = [loadBrowseJobsPage, loadJobDetailPage, loadPostJobPage];

function PreloadCoreRoutes() {
  useEffect(() => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return;
    }

    const preload = () => {
      void Promise.allSettled(coreRoutePreloads.map((loadRoute) => loadRoute()));
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(preload, { timeout: 1800 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeout = globalThis.setTimeout(preload, 400);
    return () => globalThis.clearTimeout(timeout);
  }, []);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function AppRoutes() {
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    void seedDemoData().catch((cause: unknown) => {
      setInitError(
        cause instanceof Error ? cause.message : "Local database initialization failed.",
      );
    });
  }, []);

  return (
    <>
      <ScrollToTop />
      <PreloadCoreRoutes />
      <Suspense fallback={<RouteSkeleton />}>
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<LandingPage />} />
            <Route
              path="post"
              element={
                <>
                  {initError ? (
                    <Notice title="Local database warning" tone="danger">
                      {initError}
                    </Notice>
                  ) : null}
                  <PostJobPage />
                </>
              }
            />
            <Route path="jobs" element={<BrowseJobsPage />} />
            <Route path="jobs/:id" element={<JobDetailPage />} />
            <Route path="contractors/:id" element={<ContractorProfilePage />} />
            <Route path="safety" element={<SafetyPage />} />
            <Route path="data" element={<DataPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

function RouteSkeleton() {
  const { pathname } = useLocation();
  const isJobs = pathname.startsWith("/jobs");
  const isPost = pathname.startsWith("/post");

  return (
    <main
      className="mx-auto max-w-[var(--page-max)] px-4 py-8 md:px-6 md:py-10 xl:px-10"
      aria-busy="true"
    >
      <div className="grid gap-5">
        <div className="skeleton h-36 rounded-lg" />
        {isPost ? (
          <div className="grid gap-6 lg:grid-cols-[18rem_1fr_360px]">
            <div className="skeleton h-72 rounded-card" />
            <div className="skeleton h-96 rounded-card" />
            <div className="skeleton h-72 rounded-card" />
          </div>
        ) : isJobs ? (
          <div className="grid gap-6 lg:grid-cols-[19rem_1fr]">
            <div className="skeleton h-96 rounded-card" />
            <div className="grid gap-4">
              <div className="skeleton h-40 rounded-card" />
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="skeleton h-28 rounded-card" />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            <div className="skeleton h-28 rounded-card" />
            <div className="skeleton h-28 rounded-card" />
            <div className="skeleton h-28 rounded-card" />
          </div>
        )}
      </div>
    </main>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
